import axios, { type AxiosRequestConfig } from 'axios'
import type { ApiEnvelope } from './types'
import { AppError, normalizeApiError } from './errors'

const ACCESS_TOKEN_KEY = 'huayang_access_token'
let tokenVersion = 0
export const DEFAULT_API_BASE_URL = '/api/v1'

export function resolveApiBaseURL(configuredBaseURL?: string) {
  return configuredBaseURL?.trim() || DEFAULT_API_BASE_URL
}

const http = axios.create({
  baseURL: resolveApiBaseURL(import.meta.env.VITE_API_BASE_URL),
  timeout: 12_000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
})

export function getAccessToken() {
  return sessionStorage.getItem(ACCESS_TOKEN_KEY)
}

export function setAccessToken(token: string) {
  sessionStorage.setItem(ACCESS_TOKEN_KEY, token)
}

export function clearAccessToken() {
  tokenVersion++
  sessionStorage.removeItem(ACCESS_TOKEN_KEY)
}

http.interceptors.request.use((config) => {
  const token = getAccessToken()
  if (token) config.headers.Authorization = `Bearer ${token}`
  config.headers['X-Client'] = 'huayang-admin-web'
  return config
})

let refreshing: Promise<void> | null = null

export async function refreshAccessToken() {
  if (!refreshing) {
    const version = tokenVersion
    refreshing = http
      .post<ApiEnvelope<{ accessToken: string }>>('/auth/refresh')
      .then((response) => {
        if (version !== tokenVersion) throw new AppError('登录状态已退出', 401)
        if (response.data.code !== 0 || !response.data.data?.accessToken) {
          throw new AppError(response.data.message || '登录状态已过期', response.data.code, response.data.requestId)
        }
        setAccessToken(response.data.data.accessToken)
      })
      .finally(() => {
        refreshing = null
      })
  }
  return refreshing
}

function isAuthRequest(url?: string) {
  return !!url && (url.includes('/auth/login') || url.includes('/auth/refresh'))
}

http.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (axios.isAxiosError(error) && error.response?.status === 401 && error.config && !isAuthRequest(error.config.url)) {
      const original = error.config as AxiosRequestConfig & { _retry?: boolean }
      if (!original._retry) {
        original._retry = true
        try {
          await refreshAccessToken()
          return http.request(original)
        } catch (refreshFailure) {
          const refreshError = normalizeApiError(refreshFailure)
          const code = refreshError.code
          const expired = code === 401 || code === 403
            || (code !== undefined && ((code >= 40100 && code < 40200) || (code >= 40300 && code < 40400)))
          if (!expired) return Promise.reject(refreshError)
          clearAccessToken()
          if (window.location.pathname !== '/login') {
            const redirect = encodeURIComponent(`${window.location.pathname}${window.location.search}`)
            window.location.assign(`/login?redirect=${redirect}`)
          }
        }
      }
    }
    return Promise.reject(normalizeApiError(error))
  },
)

async function request<T>(config: AxiosRequestConfig): Promise<T> {
  const response = await http.request<ApiEnvelope<T>>(config)
  const envelope = response.data

  if (envelope.code !== 0) {
    throw new AppError(envelope.message, envelope.code, envelope.requestId)
  }

  return envelope.data
}

export const api = {
  get<T>(url: string, params?: object) {
    return request<T>({ method: 'GET', url, params })
  },
  post<T, D = unknown>(url: string, data?: D) {
    return request<T>({ method: 'POST', url, data })
  },
  postLongRunning<T, D = unknown>(url: string, data?: D) {
    return request<T>({ method: 'POST', url, data, timeout: 120_000 })
  },
  postForm<T>(url: string, data: FormData, onUploadProgress?: AxiosRequestConfig['onUploadProgress']) {
    return request<T>({
      method: 'POST',
      url,
      data,
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    })
  },
  put<T, D = unknown>(url: string, data?: D) {
    return request<T>({ method: 'PUT', url, data })
  },
  uploadForm<T>(url: string, data: FormData, method: 'POST' | 'PUT') {
    return request<T>({ method, url, data, headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120_000 })
  },
  patch<T, D = unknown>(url: string, data?: D) {
    return request<T>({ method: 'PATCH', url, data })
  },
  delete<T>(url: string) {
    return request<T>({ method: 'DELETE', url })
  },
  async blob(url: string, params?: object) {
    const response = await http.request<Blob>({
      method: 'GET',
      url,
      params,
      responseType: 'blob',
    })
    return response.data
  },
}
